/**
 * One hand of no-limit hold'em, as a state machine.
 *
 * Deterministic given a seed: the same seed and the same actions replay exactly.
 * That is not a nicety — it is what makes a seasonal scenario the same scenario for
 * every player, what makes a hand history a replay rather than a story, and what
 * makes the tests below able to assert anything at all.
 *
 * No I/O. The caller drives it: ask `legalActions`, apply one, repeat until `done`.
 */
import { evaluate7 } from "../pipeline/solve/evaluator";
import { buildPots, awardPots } from "./pots";
import type { Action, Card, HandResult, SeatState, Street, TableRules } from "./types";

export interface HandOptions {
  rules: TableRules;
  /** Seat index of the button. */
  button: number;
  /** Shuffled deck; the hand deals from the front. */
  deck: Card[];
}

export class Hand {
  readonly seats: SeatState[];
  readonly rules: TableRules;
  readonly button: number;
  street: Street = "preflop";
  board: Card[] = [];
  /** Seat to act, or null when the hand is over. */
  toAct: number | null = null;
  done = false;
  result: HandResult | null = null;

  private deck: Card[];
  /** Cards burned before the flop, turn and river, in order. Recorded, not discarded. */
  readonly burned: Card[] = [];
  private next = 0;
  /** Highest total committed this round — what a caller must match. */
  private currentBet = 0;
  /** Size of the last full raise, which sets the minimum for the next one. */
  private lastRaiseSize: number;
  private startingStacks: number[];
  private log: { seat: number; action: Action; street: Street }[] = [];

  constructor(seats: SeatState[], opts: HandOptions) {
    this.seats = seats;
    this.rules = opts.rules;
    this.button = opts.button;
    this.deck = opts.deck;
    this.lastRaiseSize = opts.rules.bigBlind;
    this.startingStacks = seats.map((s) => s.stack);
    this.postBlinds();
    this.dealHoles();
    this.settleIfNoDecisionsLeft();
    /**
     * The first seat to act is the first one WITH a decision. At blind levels high
     * enough to put players all-in from the ante alone — which any structure reaches
     * eventually — the seat left of the big blind can already be all-in, and pointing
     * the action at it produced "seat has no decision" from a hand that had only just
     * been dealt.
     */
    if (!this.done) this.toAct = this.firstActorFrom(this.firstToActPreflop());
  }

  /* ------------------------------------------------------------- setup --- */

  private live(): number[] {
    return this.seats.map((s, i) => i).filter((i) => !this.seats[i]!.sittingOut);
  }

  private post(seat: number, amount: number): void {
    const s = this.seats[seat]!;
    const put = Math.min(amount, s.stack);
    s.stack -= put;
    s.committed += put;
    s.contributed += put;
    if (s.stack === 0) s.allIn = true;
  }

  private postBlinds(): void {
    const order = this.live();
    const n = order.length;
    if (this.rules.ante > 0) for (const i of order) this.post(i, this.rules.ante);

    /**
     * Heads-up reverses the blinds: the button posts the small blind and acts first
     * before the flop, last after it. Getting this wrong is the single most common
     * bug in a hand engine and it silently corrupts every heads-up solve built on it.
     */
    const sb = n === 2 ? this.button : this.seatAfter(this.button);
    const bb = n === 2 ? this.seatAfter(this.button) : this.seatAfter(sb);
    this.post(sb, this.rules.smallBlind);
    this.post(bb, this.rules.bigBlind);
    /**
     * The bet is the BIG BLIND, even when the big blind could not post all of it.
     *
     * `post` clamps to the stack, so taking the maximum of what is actually committed
     * let a short blind lower the price for the whole table: with blinds of 5/10 and a
     * big blind holding 6, everyone else could see a flop for 6 and min-raise to 16
     * instead of 20. A player all-in for less than the blind does not reduce what
     * anybody else owes — and in a tournament, whose blinds outrun stacks by design,
     * that situation arrives every time.
     */
    this.currentBet = Math.max(this.rules.bigBlind, ...this.seats.map((s) => s.committed));
    this.blindSeats = { sb, bb };
  }

  /**
   * Who posted which blind.
   *
   * Public because the presentation layer has to label the posts it animates, and
   * re-deriving "who is the small blind" from the button and the live seats in the UI
   * would be a second implementation of a rule that already lives here — the kind of
   * duplicate that goes wrong quietly at two-handed tables, where the button IS the
   * small blind. Read-only to callers by convention; the hand sets it once at deal.
   */
  blindSeats: { sb: number; bb: number } = { sb: 0, bb: 0 };

  /**
   * Deal the hole cards the way a dealer does: one card at a time, clockwise from the
   * small blind, twice around — not two cards to each seat in turn.
   *
   * With a uniformly shuffled deck the two orders are distributionally identical, so
   * this changes nobody's equity. It is here for two other reasons. A hand history
   * that records the deck should describe the same deal a live dealer would produce
   * from that deck, or the replay is not a replay of a real hand. And "which card went
   * where" is the first thing an audit reconstructs; matching the physical procedure
   * removes the need to explain a private convention.
   */
  private dealHoles(): void {
    const order = this.dealOrder();
    for (let round = 0; round < 2; round++) {
      for (const i of order) {
        const s = this.seats[i]!;
        const card = this.deck[this.next++]!;
        s.hole = round === 0 ? [card] : [...s.hole, card];
      }
    }
  }

  /** Seats receiving cards, clockwise from the small blind — the dealer's order. */
  private dealOrder(): number[] {
    const live = this.live();
    const start = live.indexOf(this.blindSeats.sb);
    if (start < 0) return live;
    return [...live.slice(start), ...live.slice(0, start)];
  }

  /**
   * Burn one card, then turn `count`.
   *
   * Burning does not make a shuffle fairer and never did — it is a defence against a
   * marked or glimpsed top card at a physical table. It is kept because the deck
   * positions in a hand history then line up with the same deal at a real table, and
   * because a burn card is part of what a player watching expects to see accounted
   * for. Burned cards are recorded, not discarded silently, so the whole 52-card
   * order in a revealed deck can be checked against what was shown.
   */
  private turnCards(count: number): void {
    this.burned.push(this.deck[this.next++]!);
    for (let i = 0; i < count; i++) this.board.push(this.deck[this.next++]!);
  }

  private seatAfter(seat: number): number {
    const n = this.seats.length;
    for (let step = 1; step <= n; step++) {
      const k = (seat + step) % n;
      if (!this.seats[k]!.sittingOut) return k;
    }
    return seat;
  }

  private firstToActPreflop(): number {
    // Heads-up: the button (small blind) is first. Otherwise: left of the big blind.
    return this.live().length === 2 ? this.button : this.seatAfter(this.blindSeats.bb);
  }

  /* ------------------------------------------------------------ actions --- */

  /** Seats that can still win the pot. */
  private inHand(): number[] {
    return this.seats.map((_, i) => i).filter((i) => !this.seats[i]!.sittingOut && !this.seats[i]!.folded);
  }

  /** Seats that can still make a decision. */
  private canAct(): number[] {
    return this.inHand().filter((i) => !this.seats[i]!.allIn);
  }

  legalActions(seat: number): Action[] {
    const s = this.seats[seat]!;
    if (this.done || this.toAct !== seat || s.folded || s.allIn) return [];
    const owed = this.currentBet - s.committed;
    const out: Action[] = [];

    out.push({ kind: "fold" });
    if (owed <= 0) out.push({ kind: "check" });

    const allInTo = s.committed + s.stack;

    /**
     * A seat that has already acted this round and still owes chips is facing a
     * SHORT all-in — anything that raised the bet by a full increment would have
     * cleared this flag for everyone else. It may call or fold and nothing more.
     *
     * The rule was already enforced when applying an action; it was missing here,
     * which is worse than useless: a caller asking "what may I do" was told it could
     * raise, and only found out by having the raise rejected. A bot driving itself
     * off this list would have been handed an illegal move.
     */
    if (s.actedThisRound && owed > 0) {
      if (s.stack > 0) out.push({ kind: "call", to: Math.min(this.currentBet, allInTo) });
      return out;
    }

    if (this.rules.actionSet === "push_fold") {
      /**
       * Push or fold means exactly that: the only way to put chips in voluntarily is
       * to put them all in. Calling is allowed only against an all-in — otherwise
       * "call" would be a limp, which this format does not have. Offering it would
       * let a bot take a line our solver never modelled, in the one format where the
       * whole claim is that it did.
       */
      const facingAllIn = this.seats.some(
        (x, i) => !x.folded && !x.sittingOut && x.allIn && i !== seat && x.committed >= this.currentBet);
      /**
       * Calling all-in for less than the bet is always available, whether or not
       * anybody else is already all-in.
       *
       * The earlier version required a standing all-in to call, and offered the shove
       * only when it would exceed the current bet — so a seat whose whole stack was
       * smaller than the big blind had exactly one legal action: fold. In a format
       * whose entire premise is "push or fold", and in tournaments where the blinds
       * double past the structure and sub-blind stacks are guaranteed, that seat
       * blinded out one forced fold at a time instead of getting its chips in.
       */
      if (owed > 0 && (facingAllIn || allInTo <= this.currentBet) && s.stack > 0) {
        out.push({ kind: "call", to: Math.min(this.currentBet, allInTo) });
      }
      if (allInTo > this.currentBet) out.push({ kind: "allin", to: allInTo });
      return out;
    }

    if (owed > 0 && s.stack > 0) out.push({ kind: "call", to: Math.min(this.currentBet, allInTo) });

    const minTo = this.currentBet + this.lastRaiseSize;
    if (allInTo > this.currentBet) {
      if (allInTo >= minTo) {
        out.push({ kind: this.currentBet === 0 ? "bet" : "raise", to: minTo <= allInTo ? minTo : allInTo });
      }
      // A stack too short for a full raise can still go in, as an all-in.
      out.push({ kind: "allin", to: allInTo });
    }
    return out;
  }

  apply(seat: number, action: Action): void {
    if (this.done) throw new Error("hand is over");
    if (this.toAct !== seat) throw new Error(`seat ${seat} cannot act; it is ${this.toAct}'s turn`);
    const s = this.seats[seat]!;
    if (s.folded || s.allIn) throw new Error(`seat ${seat} has no decision`);

    const owed = this.currentBet - s.committed;

    switch (action.kind) {
      case "fold":
        s.folded = true;
        break;
      case "check":
        if (owed > 0) throw new Error(`seat ${seat} cannot check facing ${owed}`);
        break;
      case "call":
        this.post(seat, Math.min(owed, s.stack));
        break;
      case "bet":
      case "raise":
      case "allin": {
        /**
         * A seat that has already acted and is still owed chips is facing a SHORT
         * all-in, and may only call or fold.
         *
         * `legalActions` has always got this right; `apply` did not, despite a comment
         * over there claiming it was enforced here. So the public API accepted a
         * re-raise that the same object would tell you was illegal if you asked. No
         * shipped caller exploited it — the bots and the slider both build from
         * `legalActions` — but an engine whose rules are advisory is one nobody can
         * build on.
         */
        if (s.actedThisRound && owed > 0 && this.rules.actionSet === "full") {
          throw new Error(
            `seat ${seat} is facing a short all-in and may only call or fold`);
        }
        const to = action.to ?? s.committed + s.stack;
        const target = Math.min(to, s.committed + s.stack);
        if (target <= this.currentBet && target < s.committed + s.stack) {
          throw new Error(`raise to ${target} does not exceed the bet of ${this.currentBet}`);
        }
        if (this.rules.actionSet === "full" && target < this.currentBet + this.lastRaiseSize
            && target < s.committed + s.stack) {
          throw new Error(`raise to ${target} is below the minimum of ${this.currentBet + this.lastRaiseSize}`);
        }
        const raiseSize = target - this.currentBet;
        this.post(seat, target - s.committed);

        /**
         * An all-in for LESS than a full raise does not reopen the betting.
         *
         * Players who already acted and are facing that short all-in may call or
         * fold, but may not raise again. This is a real rule that changes real
         * decisions, and engines that skip it quietly let a player re-raise off a
         * technicality — which would then be baked into every strategy trained on it.
         */
        if (raiseSize >= this.lastRaiseSize) {
          this.lastRaiseSize = raiseSize;
          for (const i of this.canAct()) if (i !== seat) this.seats[i]!.actedThisRound = false;
        }
        this.currentBet = Math.max(this.currentBet, target);
        break;
      }
    }

    s.actedThisRound = true;
    this.log.push({ seat, action, street: this.street });
    this.advance();
  }

  /* ------------------------------------------------------------- flow --- */

  private advance(): void {
    if (this.inHand().length === 1) { this.settle(false); return; }

    const pending = this.canAct().filter(
      (i) => !this.seats[i]!.actedThisRound || this.seats[i]!.committed < this.currentBet);

    if (pending.length > 0) {
      this.toAct = this.nextToAct();
      return;
    }
    this.closeStreet();
  }

  private nextToAct(): number {
    const n = this.seats.length;
    const from = this.toAct ?? this.button;
    for (let step = 1; step <= n; step++) {
      const k = (from + step) % n;
      const s = this.seats[k]!;
      if (s.sittingOut || s.folded || s.allIn) continue;
      if (!s.actedThisRound || s.committed < this.currentBet) return k;
    }
    return from;
  }

  private closeStreet(): void {
    if (this.street === "river") { this.settle(true); return; }

    for (const s of this.seats) { s.committed = 0; s.actedThisRound = false; }
    this.currentBet = 0;
    this.lastRaiseSize = this.rules.bigBlind;

    this.street = this.street === "preflop" ? "flop" : this.street === "flop" ? "turn" : "river";
    this.turnCards(this.street === "flop" ? 3 : 1);

    this.settleIfNoDecisionsLeft();
    if (this.done) return;
    // After the flop, action starts with the first live seat left of the button.
    this.toAct = this.nextFrom(this.button);
  }

  /** The first seat at or after `from` that still has a decision to make. */
  private firstActorFrom(from: number): number {
    const n = this.seats.length;
    for (let step = 0; step < n; step++) {
      const k = (from + step) % n;
      const s = this.seats[k]!;
      if (!s.sittingOut && !s.folded && !s.allIn) return k;
    }
    return from;
  }

  private nextFrom(seat: number): number {
    const n = this.seats.length;
    for (let step = 1; step <= n; step++) {
      const k = (seat + step) % n;
      const s = this.seats[k]!;
      if (!s.sittingOut && !s.folded && !s.allIn) return k;
    }
    return seat;
  }

  /**
   * When nobody has a decision left — everyone is all-in, or all but one has folded —
   * the remaining cards are dealt and the hand is settled without asking anyone.
   */
  private settleIfNoDecisionsLeft(): void {
    if (this.inHand().length === 1) { this.settle(false); return; }
    if (this.canAct().length > 1) return;
    // One player with chips left and everyone else all-in: no betting can occur.
    if (this.canAct().length === 1) {
      const solo = this.canAct()[0]!;
      if (this.seats[solo]!.committed < this.currentBet) return;   // still owes a call
    }
    // Run it out street by street, with a burn before each — the same cards a dealer
    // would produce from this deck, rather than five cards taken off the top.
    while (this.board.length < 5) this.turnCards(this.board.length === 0 ? 3 : 1);
    this.settle(true);
  }

  private settle(showdown: boolean): void {
    const liveSet = new Set(this.inHand());
    const { pots, refunds } = buildPots(this.seats.map((s) => s.contributed), liveSet);
    const rank = (seat: number) =>
      showdown && this.board.length === 5
        ? evaluate7([...this.seats[seat]!.hole, ...this.board])
        : (liveSet.has(seat) ? 1 : -1);

    const won = awardPots(pots, rank, this.seats.length, this.button, this.rules.chipSize);
    /**
     * Refunds first, then winnings. Both are stack increases, so the order does not
     * change the arithmetic — but keeping them separate keeps the hand history honest:
     * chips handed back were never won, and a player whose uncalled bet came back has
     * not beaten anybody.
     */
    for (let i = 0; i < this.seats.length; i++) this.seats[i]!.stack += refunds[i]! + won[i]!;

    this.result = {
      net: this.seats.map((s, i) => s.stack - this.startingStacks[i]!),
      pots: pots.map((p) => {
        let best = -Infinity, winners: number[] = [];
        for (const s of p.eligible) {
          const r = rank(s);
          if (r > best) { best = r; winners = [s]; }
          else if (r === best) winners.push(s);
        }
        return { amount: p.amount, winners };
      }),
      showdown: showdown && liveSet.size > 1,
      board: [...this.board],
    };
    this.toAct = null;
    this.done = true;
  }

  history(): { seat: number; action: Action; street: Street }[] { return [...this.log]; }
  potSize(): number { return this.seats.reduce((a, s) => a + s.contributed, 0); }
}

/** A fresh seat, ready for a hand. */
export function seatFrom(config: SeatState["config"], stack: number, sittingOut = false): SeatState {
  return {
    config, stack, committed: 0, contributed: 0, hole: [],
    folded: false, allIn: false, actedThisRound: false, sittingOut,
  };
}
